<script module lang="ts">
	import { store } from '../api/client';

	// Till settings (T26, UC-59, #774): anyone signed in chooses the till's
	// language and theme in its first row (decision
	// 2026-10-23-till-settings-for-everyone). A manager also sees each setup
	// step of this till with its state, and opens any step again. It replaces
	// the work area, as Orders and Printers do, and lists one row per step the
	// store offers that the screen's step list knows (src/till/setupSteps.ts).
	// A cashier's list, or one whose read is refused, holds the first two
	// rows alone, and a cashier's reads no setup. Open on Printers shows T25; Open
	// on Sharing shows T28, and Open on Cash point T32, as a Sheet over it.
	// A manager's list ends with Drivers (T34, #1003), whatever the steps'
	// read says, since the drivers are the server's, not the store's; its
	// Open shows T34 as a Sheet over it.
	//
	// Refresh from Bosla follows Language and theme, for everyone signed in
	// (UC-13.R21, #1061): in place of Open its Button, as T13's Check again,
	// asks the till's order store to hear from Bosla now (POST /store/refresh).
	// It is pending while the store's heartbeat goes, then reads Up to date; a
	// press soon after another is sent all the same, since the server is the
	// one that limits how often it builds the tags again.

	/** What the panel shows for the read: nothing for its first 300 ms, then the compass, then its answer. */
	export function settingsReadState(loading: boolean, slow: boolean, failed: boolean) {
		return loading ? (slow ? 'loading' : 'waiting') : failed ? 'failed' : 'ready';
	}

	/** How a press of Refresh from Bosla came out: the store answered 204, or it did not. */
	export type RefreshOutcome = 'done' | 'failed';

	/** Asks the order store to hear from Bosla now; anything but a 204 says Bosla did not answer. */
	export async function refreshFromBosla(): Promise<RefreshOutcome> {
		try {
			const { response } = await store.POST('/store/refresh');
			return response.status === 204 ? 'done' : 'failed';
		} catch {
			return 'failed';
		}
	}

	/** How long a press waits before its button says Refreshing…, as every till wait does. */
	export const REFRESH_SLOW_MS = 300;

	/** How long Up to date shows before the button reads Refresh again. */
	export const REFRESHED_FOR_MS = 2000;

	/** The Refresh button's state: pending only once a press on its way is slow, done for a while after a 204. */
	export function refreshButtonState(
		inFlight: boolean,
		slow: boolean,
		refreshed: boolean,
	): 'idle' | 'pending' | 'done' {
		if (inFlight) return slow ? 'pending' : 'idle';
		return refreshed ? 'done' : 'idle';
	}

	/** What follows the Language and theme row: a manager's steps, or the wait or failure of their read. */
	export function settingsParts(
		isManager: boolean,
		state: ReturnType<typeof settingsReadState>,
		forbidden: boolean,
	): { steps: boolean; loading: boolean; failed: boolean; drivers: boolean } {
		const allowed = isManager && !forbidden;
		return {
			steps: allowed && state === 'ready',
			loading: allowed && state === 'loading',
			failed: allowed && state === 'failed',
			drivers: allowed,
		};
	}
</script>

<script lang="ts">
	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Chip from '../components/Chip.svelte';
	import Loading from '../components/Loading.svelte';
	import Row from '../components/Row.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import { fill, message, type Language } from '../i18n/messages';
	import type { TillSetup } from '../till/setup.svelte';
	import { knownSteps, type StepContext } from '../till/setupSteps';
	import { driversRowState } from '../till/tillDrivers.svelte';

	interface Props {
		language: Language;
		isManager: boolean;
		display: string;
		setup: TillSetup;
		context: StepContext;
		onBack: () => void;
		onOpen: (target: 'printers' | 'sharing' | 'cash_point') => void;
		onDisplay: () => void;
		/** How many drivers the shop has, once their read answered; null before. */
		driverCount: number | null;
		/** The drivers' read was refused FORBIDDEN: no manager now, so no row. */
		driversForbidden: boolean;
		onDrivers: () => void;
	}
	let {
		language,
		isManager,
		setup,
		context,
		display,
		onBack,
		onOpen,
		onDisplay,
		driverCount,
		driversForbidden,
		onDrivers,
	}: Props = $props();
	const readState = $derived(settingsReadState(setup.loading, setup.slow, setup.failed));
	const parts = $derived(settingsParts(isManager, readState, setup.forbidden));
	const driversRow = $derived(driversRowState(driverCount));

	// A press on its way shows Refreshing… only past REFRESH_SLOW_MS, as every
	// till wait does, and a second press while one is on its way sends nothing.
	let inFlight = $state(false);
	let slow = $state(false);
	let refreshed = $state(false);
	let refreshFailed = $state(false);
	let slowTimer: ReturnType<typeof setTimeout> | undefined;
	let refreshedTimer: ReturnType<typeof setTimeout> | undefined;
	$effect(() => () => {
		clearTimeout(slowTimer);
		clearTimeout(refreshedTimer);
	});
	const refreshState = $derived(refreshButtonState(inFlight, slow, refreshed));

	async function refresh() {
		if (inFlight) return;
		clearTimeout(refreshedTimer);
		inFlight = true;
		refreshed = false;
		refreshFailed = false;
		slowTimer = setTimeout(() => (slow = true), REFRESH_SLOW_MS);
		const outcome = await refreshFromBosla();
		clearTimeout(slowTimer);
		inFlight = slow = false;
		refreshFailed = outcome === 'failed';
		refreshed = outcome === 'done';
		if (refreshed) refreshedTimer = setTimeout(() => (refreshed = false), REFRESHED_FOR_MS);
	}
</script>

<div class="settings" data-testid="panel-till-settings" data-scroll="">
	<div class="bar">
		<Button
			testId="till-settings-back"
			icon="back"
			mirrorIcon={language === 'en'}
			label={message(language, 'printersBack')}
			tone="secondary"
			size="sm"
			onPress={onBack}
		/>
		<h2>{message(language, 'tillSettings')}</h2>
	</div>

	<TablePanel
		columns={[
			{ label: message(language, 'tillSettingsItem'), w: 'minmax(0, 1fr)' },
			{ label: message(language, 'tillSettingsState'), w: 'minmax(0, 2fr)' },
			{ label: '', w: 'auto' },
		]}
	>
		<Row testId="till-settings-row-display">
			<strong>{message(language, 'tillDisplayTitle')}</strong>
			<div class="state">
				<span data-testid="till-settings-state-display">{display}</span>
			</div>
			<Button
				testId="till-settings-open-display"
				label={message(language, 'setupOpen')}
				tone="secondary"
				size="sm"
				onPress={onDisplay}
			/>
		</Row>
		<Row testId="till-settings-row-refresh">
			<strong>{message(language, 'tillRefreshTitle')}</strong>
			<div class="state">
				<span data-testid="till-settings-state-refresh"
					>{message(language, refreshFailed ? 'tillRefreshFailed' : 'tillRefreshState')}</span
				>
			</div>
			<Button
				testId="till-settings-refresh"
				label={message(language, 'tillRefresh')}
				tone="secondary"
				size="sm"
				state={refreshState}
				pendingLabel={message(language, 'tillRefreshing')}
				doneLabel={message(language, 'tillRefreshed')}
				onPress={() => void refresh()}
			/>
		</Row>
		{#if parts.steps}
			{#each knownSteps(setup.setup) as step (step.id)}
				<Row testId="till-settings-row-{step.id}">
					<strong>{message(language, step.name)}</strong>
					<div class="state">
						<span data-testid="till-settings-state-{step.id}">{step.state(language, context)}</span>
						{#if step.flag(context)}<Chip
								text={message(language, step.flagText)}
								tone="flag"
								testId="till-settings-flag-{step.id}"
							/>{/if}
					</div>
					<Button
						testId="till-settings-open-{step.id}"
						label={message(language, 'setupOpen')}
						tone="secondary"
						size="sm"
						onPress={() => onOpen(step.open)}
					/>
				</Row>
			{/each}
		{/if}
		{#if parts.drivers && !driversForbidden}
			<Row testId="till-settings-row-drivers">
				<strong>{message(language, 'tillDriversTitle')}</strong>
				<div class="state">
					<span data-testid="till-settings-state-drivers"
						>{driversRow ? fill(message(language, driversRow.key), driversRow.count) : ''}</span
					>
				</div>
				<Button
					testId="till-settings-open-drivers"
					label={message(language, 'setupOpen')}
					tone="secondary"
					size="sm"
					onPress={onDrivers}
				/>
			</Row>
		{/if}
	</TablePanel>
	{#if parts.loading}
		<Loading
			label={message(language, 'tillSettingsLoading')}
			caption={message(language, 'tillSettingsLoading')}
		/>
	{:else if parts.failed}
		<Band
			tone="flag"
			testId="till-settings-error"
			action={setup.retryable ? message(language, 'doneRetry') : undefined}
			actionTestId="till-settings-retry"
			onAction={setup.retryable ? () => void setup.read() : undefined}
		>
			{message(language, 'tillSettingsFailed')}
		</Band>
	{/if}
</div>

<style>
	.settings {
		flex: 1;
		min-block-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		padding: var(--space-7);
		overflow-y: auto;
	}
	.bar,
	.state {
		display: flex;
		align-items: center;
		gap: var(--space-4);
	}
	h2 {
		margin: 0;
		font-size: var(--text-h4);
		font-weight: 700;
	}
	.state {
		flex-wrap: wrap;
		color: var(--text-body);
	}
</style>
